import { useCallback, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { ReactComponent as EmptyCourses } from "../assets/images/illustrations/3D/empty-courses.svg";
import { CourseType, EditionStatus } from "../common/constants";
import { Each } from "../common/Each";
import useWindowDimensions from "../common/hooks/useWindowDimensions";
import EmptyContent from '../components/EmptyContent';
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import LessonsCarousel from '../components/LessonsCarousel';
import Loader from "../components/Loader";
import Select from "../components/Select";
import typo from "../typography.module.css";
import styles from "./Courses.module.css";
import Card from "../components/cards/Card";
import { ReactComponent as ChevronIcon } from "../assets/images/icons/ic-chevron.svg"
import TextInput from "../components/TextInput";

const Courses = () => {
  const { t } = useTranslation();
  useWindowDimensions(); // hook kept for potential responsive behavior, value unused
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [filterState, setFilterState] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const filterOptions = [
    { id: 0, name: t("courses.all"), value: 0 },
    { id: 1, name: t("courses.masterclassCourses"), value: 1 },
    { id: 2, name: t("courses.professionalCourses"), value: 2 },
    { id: 3, name: t("courses.bootcamp"), value: 3 },
    { id: 4, name: t("courses.archive"), value: 4 },
  ]

  // restituisce i corsi filtrati in base allo stato dello switch (senza search)
  const baseFilterCourses = useCallback((index) => {
    let filtered = [];
    switch (index) {
      case 0:
        return courses.map((c) => ({
          ...c,
          editions: c.editions.filter((e) => e.edition.status === EditionStatus.Public)
        }));
      case 1:
        filtered = courses.filter(c => c.course.type === CourseType.Masterclass).map((c) => ({
          ...c,
          editions: c.editions.filter((e) => e.edition.status === EditionStatus.Public)
        }));
        return filtered;
      case 2:
        filtered = courses.filter(c => c.course.type === CourseType.Professional).map((c) => ({
          ...c,
          editions: c.editions.filter((e) => e.edition.status === EditionStatus.Public)
        }));
        return filtered;
      case 3:
        filtered = courses.filter(c => c.course.type === CourseType.Bootcamp).map((c) => ({
          ...c,
          editions: c.editions.filter((e) => e.edition.status === EditionStatus.Public)
        }));
        return filtered;
      case 4:
        filtered = courses.map((c) => ({
          ...c,
          editions: c.editions.filter((e) => [EditionStatus.Ended, EditionStatus.Locked].includes(e.edition.status))
        }));
        return filtered;
      default:
        return courses;
    }
  }, [courses]);

  const searchCourses = useCallback((query, base) => {
    const q = query.toLowerCase();
    return base
      .map(course => {
        const courseNameMatch = course.course.name?.toLowerCase().includes(q);
        const editions = course.editions.map(ed => {
          const editionNameMatch = ed.edition.name?.toLowerCase().includes(q);
          const modules = ed.modules.filter(m => (m.name || "").toLowerCase().includes(q) || (m.edition?.name || "").toLowerCase().includes(q));
          if (editionNameMatch || modules.length > 0) {
            return { ...ed, modules };
          }
          return null;
        }).filter(Boolean);
        if (courseNameMatch || editions.length > 0) {
          return { ...course, editions };
        }
        return null;
      })
      .filter(Boolean);
  }, []);

  useEffect(() => {
    const getLessons = async () => {
      setLessonLoading(true);
      try {
        let lessons = await api.get(`/teacher/lessons?ends_after=${new Date().toISOString()}&limit=16`);
        setLessons(lessons.sort((a, b) => {
          return new Date(a.starts_at) - new Date(b.starts_at)
        }))
      } catch (e) {
        console.error(e);
      }
      setLessonLoading(false);
    };

    const getCourses = async () => {
      setCoursesLoading(true);
      try {
        const modules = await api.get("/teacher/modules");
        setCourses(buildCatalog(modules, { editionsOrder: "desc", modulesOrder: "asc" }));
        setFilteredCourses(buildCatalog(modules, { editionsOrder: "desc", modulesOrder: "asc" }).map((c) => {
          return { ...c, editions: c.editions.filter((e) => e.edition.status === EditionStatus.Public) };
        }));
        setFilterState(0)

      } catch (e) {
        console.error(e);
      }
      setCoursesLoading(false);
    };

    getLessons();
    getCourses();
  }, []);

  function buildCatalog(items, {
    editionsOrder = "asc", // "asc" | "desc" sul locked_at
    modulesOrder = "asc",  // "asc" | "desc" sulla position
  } = {}) {
    const dirE = editionsOrder === "desc" ? -1 : 1;
    const dirM = modulesOrder === "desc" ? -1 : 1;

    const toTime = (v) => {
      if (!v) return Number.POSITIVE_INFINITY; // edition senza locked_at va in fondo in "asc"
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };

    const groupedByCourse = items.reduce((acc, item) => {
      const course = item.edition?.course;
      const edition = item.edition;
      if (!course || !edition) return acc;

      const courseId = course.id;
      const editionId = edition.id;

      // crea contenitore corso
      if (!acc[courseId]) {
        acc[courseId] = {
          course: {
            id: course.id,
            name: course.name,
            thumbnail: course.thumbnail,
            type: course.type,
            slug: course.slug,
            status: course.status,
          },
          editionsMap: {}, // temporaneo per raggruppare
        };
      }

      // crea contenitore edition
      const courseBucket = acc[courseId];
      if (!courseBucket.editionsMap[editionId]) {
        courseBucket.editionsMap[editionId] = {
          edition: {
            id: edition.id,
            name: edition.name,
            status: edition.status,
            expired_at: edition.expired_at,
            locked_at: edition.locked_at,
          },
          modules: [],
        };
      }

      // push modulo
      courseBucket.editionsMap[editionId].modules.push(item);
      return acc;
    }, {});

    // ordina moduli per position e editions per locked_at
    const result = Object.values(groupedByCourse).map((courseBucket) => {
      // ordina moduli dentro ciascuna edition
      const editions = Object.values(courseBucket.editionsMap).map((ed) => {
        ed.modules.sort((a, b) => {
          const pa = Number.isFinite(+a.position) ? +a.position : Number.POSITIVE_INFINITY;
          const pb = Number.isFinite(+b.position) ? +b.position : Number.POSITIVE_INFINITY;
          return (pa - pb) * dirM;
        });
        return ed;
      });

      // ordina le edition per locked_at
      editions.sort((a, b) => (toTime(a.edition.expired_at) - toTime(b.edition.expired_at)) * dirE);

      // ritorna struttura finale senza la mappa interna
      return {
        course: courseBucket.course,
        editions,
      };
    });

    // opzionale: ordina i corsi per nome (crescente)
    result.sort((a, b) => a.course.name.localeCompare(b.course.name, undefined, { sensitivity: "base" }));

    return result;
  }


  const handleEditionFilterChange = useCallback((index) => {
    const base = baseFilterCourses(index);
    setFilterState(index);
    if (searchQuery.trim()) {
      setFilteredCourses(searchCourses(searchQuery, base));
    } else {
      setFilteredCourses(base);
    }
  }, [baseFilterCourses, searchQuery, searchCourses]);

  const onSearch = (query) => {
    setSearchQuery(query);
    const base = baseFilterCourses(filterState);
    if (!query.trim()) {
      setFilteredCourses(base);
      return;
    }
    setFilteredCourses(searchCourses(query, base));
  }; 


  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>I miei corsi</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={typo.title}>{t("courses.title")}</div>

            <LessonsCarousel lessons={lessons} loading={lessonsLoading} />

            <div className={styles.courses}>
              <div className={styles.filtersRow}>
                <div className={styles.filterSelect}>
                  <Select
                    selected={filterOptions.find((option) => option.value === filterState)}
                    options={filterOptions}
                    placeholder={t("courses.all")}
                    onSelect={(option) => {
                      handleEditionFilterChange(option?.value ?? 0)
                    }}
                  />
                </div>
                <div className={styles.filterSearch}>
                  <TextInput
                    placeholder="Cerca corso, edizione o modulo"
                    type="search"
                    value={searchQuery}
                    onKeyUp={onSearch}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
              <div className={styles.courseList}>
                {coursesLoading && (
                  <div className={styles.loaderContainer}>
                    <Loader />
                  </div>
                )}

                {!coursesLoading && (
                  <>
                    {filteredCourses.length > 0 && (
                      <Each of={filteredCourses.filter(c => c.editions.length > 0)} render={(course) => {
                        return (
                          <Card title={
                            <div className={styles.courseHeader} onClick={() => {
                              setFilteredCourses(prevCourses => prevCourses.map(c => {
                                if (c.course.id === course.course.id) {
                                  return { ...c, expanded: !c.expanded };
                                }
                                return c;
                              }));
                            }}>
                              <img src={course.course.thumbnail} alt="" className={styles.courseThumbnail} />
                              <div className={styles.courseName}>{course.course.name}</div>
                            </div>
                          }>
                            {course.course.type === CourseType.Professional &&
                              <div className={`${styles.courseContent} ${course.expanded ? styles.expanded : ""}`}>
                                <div className={styles.editions}>
                                  <div className={`${styles.edition} ${filteredCourses.find(c => c.course.id === course.course.id && !c.selectedEdition) ? styles.selected : ""}`} onClick={() => {
                                    setFilteredCourses(prevCourses => prevCourses.map(c => {
                                      if (c.course.id === course.course.id) {
                                        return { ...c, selectedEdition: null };
                                      }
                                      return c;
                                    }));
                                  }}>
                                    TUTTE LE EDIZIONI
                                  </div>
                                  <Each of={course.editions} render={(edition) => {
                                    return (
                                      <div className={`${styles.edition} ${filteredCourses.find(c => c.course.id === course.course.id && c.selectedEdition === edition.edition.id) ? styles.selected : ""}`} onClick={() => {
                                        setFilteredCourses(prevCourses => prevCourses.map(c => {
                                          if (c.course.id === course.course.id) {
                                            return { ...c, selectedEdition: edition.edition.id };
                                          }
                                          return c;
                                        }));
                                      }}>
                                        {edition.edition.name} {edition.edition.status === EditionStatus.Ended && "(TERMINATA)"} {edition.edition.status === EditionStatus.Locked && "(BLOCCATA)"}
                                      </div>
                                    )
                                  }} />
                                </div>
                                <div className={styles.modules}>
                                  <div className={typo.headline}>MODULI</div>
                                  <Each of={course.editions.flatMap(e => e.modules).filter(m => course.selectedEdition ? m.edition.id === course.selectedEdition : true)} render={(module) => {
                                    return (
                                      <div className={styles.module} onClick={() => navigate(`/courses/${module.id}`)}>
                                        <div className={styles.moduleName}>
                                          {module.name}
                                          <div className={typo.caption} style={{ opacity: 0.75 }}>{module.edition.name}</div>
                                        </div>
                                        <ChevronIcon className={styles.chevronIcon} />
                                      </div>
                                    )
                                  }} />
                                </div>
                              </div>
                            }
                            {course.course.type !== CourseType.Professional &&
                              <div className={`${styles.courseContent} ${course.expanded ? styles.expanded : ""}`}>
                                <div className={styles.modules}>
                                  <div className={typo.headline}>EDIZIONI</div>
                                  <Each of={course.editions.flatMap(e => e.modules).filter(m => course.selectedEdition ? m.edition.id === course.selectedEdition : true)} render={(module) => {
                                    return (
                                      <div className={styles.module} onClick={() => navigate(`/courses/${module.id}`)}>
                                        <div className={styles.moduleName}>
                                          {module.edition.name}  {module.edition.status === EditionStatus.Ended && "(TERMINATA)"} {module.edition.status === EditionStatus.Locked && "(BLOCCATA)"}
                                        </div>
                                        <ChevronIcon className={styles.chevronIcon} />
                                      </div>
                                    )
                                  }} />
                                </div>
                              </div>
                            }
                          </Card>
                        )
                      }} />
                    )}
                    {filteredCourses.length === 0 && (
                      <EmptyContent
                        Illustration={EmptyCourses}
                        message={
                          <>
                            {filterState === 0 && `Non hai ancora un corso in programma, contatta la segreteria per scoprire come puoi aggiungerli`}
                            {filterState === 1 && `Non hai ancora una masterclass in programma, contatta la segreteria per scoprire come puoi aggiungerla`}
                            {filterState === 2 && `Non hai ancora un corso professionalizzante in programma, contatta la segreteria per scoprire come puoi aggiungerlo`}
                            {filterState === 3 && `Non ci sono ancora corsi conculusi.`}
                          </>
                        }
                        imageStyle={{ width: "192px" }}
                        action={{
                          label: "CONTATTA",
                          onClick: () => navigate("/messages"),
                          buttonProps: {
                            style: { padding: "0.75rem 3rem" }
                          }
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
};

export default Courses;
